import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/pages/Services";
import { chargeFor } from "@/pages/Charges";
import { Landmark } from "lucide-react";
import { Screen, Card, PinPad, rs } from "./ui";
import { BalanceCard, Label, Field, Chips, Segment, Radio, FeeBox, CTA } from "./kit";
import { Slip } from "./Slip";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

function Review({ data, charge, onBack, onPay, busy }) {
  const [mpin, setMpin] = useState("");
  useEffect(() => { if (mpin.length === 4) onPay(mpin); }, [mpin]); // eslint-disable-line
  useEffect(() => { if (!busy) setMpin(""); }, [busy]);
  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-black/40" data-testid="m-payout-review">
      <div className="rounded-t-[32px] bg-[#F6F5F1] px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5">
        <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-black/10" />
        <div className="text-center text-sm text-slate-500">Sending to {data.name}</div>
        <div className="text-center font-display text-4xl font-bold" data-testid="m-review-amount">{rs(data.amount)}</div>
        <div className="mt-3 space-y-1 rounded-2xl bg-white p-3 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">A/c</span><span className="font-mono">{data.acc}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Mode</span><span>{data.mode}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Charge</span><span className="font-mono" data-testid="m-review-charge">{rs(charge)}</span></div>
          <div className="flex justify-between font-bold"><span>Total debit</span><span className="font-mono" data-testid="m-review-total">{rs(data.amount + charge)}</span></div>
        </div>
        <div className="my-4 text-center text-sm font-semibold">{busy ? "Sending…" : "Enter MPIN to confirm"}</div>
        <PinPad value={mpin} onChange={busy ? () => {} : setMpin} testid="m-mpin" />
        <button onClick={onBack} disabled={busy} className="mt-4 w-full py-2 text-sm font-semibold text-slate-500" data-testid="m-review-cancel">Cancel</button>
      </div>
    </div>
  );
}

export function MMoveToBank() {
  const { user, wallet, refreshBalance } = useAuth();
  const meta = useMeta();
  const types = [["saved", "Saved account", "payout_saved"], ["direct", "Direct transfer", "payout_direct"]].filter((t) => user.services?.[t[2]] !== false);
  const [type, setType] = useState(types[0]?.[0]);
  const [accounts, setAccounts] = useState([]);
  const [plan, setPlan] = useState(null);
  const [lim, setLim] = useState(null);
  const [f, setF] = useState({ wallet: "aeps", account_id: "", amount: "", mode: "IMPS" });
  const [d, setD] = useState({ bank: "", account_no: "", ifsc: "", holder_name: "" });
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [slip, setSlip] = useState(null);
  const [geo, setGeo] = useState({ latitude: "", longitude: "" });
  useEffect(() => {
    api.get("/settlement-accounts").then(({ data }) => setAccounts(data)).catch(() => {});
    api.get("/payout/charges").then(({ data }) => setPlan(data)).catch(() => {});
    api.get("/limits").then(({ data }) => setLim(data.payout)).catch(() => {});
    navigator.geolocation?.getCurrentPosition((p) => setGeo({ latitude: p.coords.latitude.toFixed(4), longitude: p.coords.longitude.toFixed(4) }), () => {});
  }, []);
  const amt = Number(f.amount || 0);
  const charge = amt > 0 ? chargeFor(plan, f.mode, amt) : 0;
  const acc = accounts.find((a) => a.id === f.account_id);
  const next = (e) => {
    e.preventDefault();
    if (lim && (amt < lim.min || amt > lim.max)) return toast.error(`Aap ₹${fmt(lim.min)} se ₹${fmt(lim.max)} tak transfer kar sakte hain`);
    if (charge === null) return toast.error(`No ${f.mode} charge set for this amount — contact admin`);
    if (type === "saved" && !acc) return toast.error("Select a bank account");
    setReview(type === "saved" ? { name: acc.holder_name, acc: acc.account_no, amount: amt, mode: f.mode } : { name: d.holder_name, acc: d.account_no, amount: amt, mode: f.mode });
  };
  const pay = async (mpin) => {
    setBusy(true);
    try {
      const target = type === "direct" ? { direct: d, account_id: "" } : { account_id: f.account_id };
      const { data } = await api.post("/payout", { ...f, ...target, ...geo, amount: amt, mpin });
      setReview(null); setSlip(data); refreshBalance();
      setF({ ...f, amount: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!types.length) return <Screen title="Move to Bank" dark><Card className="text-sm text-slate-500" data-testid="m-payout-none">No Move to Bank option is enabled for your account. Contact admin.</Card></Screen>;
  const canSaved = types.some((t) => t[0] === "saved");
  const canDirect = types.some((t) => t[0] === "direct");
  return (
    <Screen title="Move to Bank" dark testid="m-move">
      <form onSubmit={next}>
        <BalanceCard wallet={f.wallet} />
        <div className="mt-3"><Segment items={[["aeps", "AEPS Wallet"], ["main", "Main Wallet"]]} value={f.wallet} onChange={(v) => setF({ ...f, wallet: v })} testid="m-payout-wallet" /></div>
        <Label right={canDirect && canSaved && <button type="button" onClick={() => setType(type === "direct" ? "saved" : "direct")} className="text-sm font-semibold text-[#F26B1D]" data-testid="m-payout-toggle">{type === "direct" ? "Saved accounts" : "+ Add New"}</button>}>
          {type === "direct" ? "Enter Bank Details" : "Select Bank Account"}
        </Label>
        {type === "saved" ? (
          accounts.length ? (
            <div className="space-y-2" data-testid="m-payout-accounts">
              {accounts.map((a) => (
                <button type="button" key={a.id} onClick={() => setF({ ...f, account_id: a.id })} className={`flex w-full items-center gap-3 rounded-xl border bg-white p-3 text-left ${f.account_id === a.id ? "border-[#F26B1D]" : "border-slate-200"}`} data-testid={`m-acc-${a.id}`}>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-slate-100"><Landmark className="h-5 w-5" /></span>
                  <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{a.bank}</span><span className="block truncate text-xs text-slate-500">{a.holder_name} · <span className="font-mono">{a.account_no}</span></span></span>
                  <Radio on={f.account_id === a.id} />
                </button>
              ))}
            </div>
          ) : <div className="rounded-xl bg-slate-100 p-4 text-sm text-slate-500" data-testid="m-no-accounts">No saved account.{canDirect ? " Tap “+ Add New” to send directly." : ""}</div>
        ) : (
          <div className="space-y-3" data-testid="m-payout-direct">
            <Field list="m-banks" placeholder="Bank name" value={d.bank} onChange={(e) => setD({ ...d, bank: e.target.value })} required minLength={3} data-testid="m-direct-bank" />
            <datalist id="m-banks">{(meta?.banks || []).map((b) => <option key={b} value={b} />)}</datalist>
            <Field placeholder="Account holder name" value={d.holder_name} onChange={(e) => setD({ ...d, holder_name: e.target.value })} required data-testid="m-direct-holder" />
            <Field placeholder="Account number" inputMode="numeric" value={d.account_no} onChange={(e) => setD({ ...d, account_no: e.target.value.replace(/\D/g, "") })} required className="font-mono" data-testid="m-direct-account" />
            <Field placeholder="IFSC code" maxLength={11} value={d.ifsc} onChange={(e) => setD({ ...d, ifsc: e.target.value.toUpperCase() })} required className="font-mono uppercase" data-testid="m-direct-ifsc" />
          </div>
        )}
        <Label>Enter Amount</Label>
        <Field prefix="₹" type="number" inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required className="text-xl font-semibold" data-testid="m-payout-amount" />
        <Chips values={[1001, 5000, 10000, 20000].filter((v) => !lim || v <= lim.max)} value={f.amount} onPick={(v) => setF({ ...f, amount: v })} testid="m-payout-chips" />
        {lim && <div className="mt-3 text-xs font-medium text-slate-500" data-testid="m-payout-limit">Aap ₹{fmt(lim.min)} se ₹{fmt(lim.max)} tak transfer kar sakte hain (per transaction)</div>}
        <div className="mt-4"><Segment items={[["IMPS", "IMPS"], ["NEFT", "NEFT"], ["RTGS", "RTGS"]]} value={f.mode} onChange={(v) => setF({ ...f, mode: v })} testid="m-mode" /></div>
        {amt > 0 && (charge === null ? <div className="mt-4 text-sm text-red-600" data-testid="m-payout-charge">No {f.mode} charge set for this amount</div> : <FeeBox fee={charge} total={amt + charge} />)}
        <CTA type="submit" data-testid="m-payout-continue">Continue</CTA>
      </form>
      {review && <Review data={review} charge={charge || 0} busy={busy} onBack={() => setReview(null)} onPay={pay} />}
      {slip && <Slip txn={slip} onClose={() => setSlip(null)} />}
    </Screen>
  );
}
